import type { AcessoPortaria, MotivoAcesso, ResultadoLeitura, ResumoAcesso } from './types';

// ————————————————— acesso (portaria_acesso) —————————————————

function num(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function txt(v: unknown): string | null {
  if (v == null) return null;
  const s = String(v);
  return s;
}

function normalizarResumo(v: unknown): ResumoAcesso | null {
  if (!v || typeof v !== 'object') return null;
  const r = v as Record<string, unknown>;
  return {
    ingressos_validos: num(r.ingressos_validos),
    ja_entraram: num(r.ja_entraram),
    ainda_nao_chegaram: num(r.ainda_nao_chegaram),
    pendentes_de_pagamento: num(r.pendentes_de_pagamento),
    ultima_entrada: txt(r.ultima_entrada),
  };
}

// Lê o retorno de `portaria_acesso` sem confiar no formato exato.
export function normalizarAcesso(v: unknown): AcessoPortaria | null {
  if (!v || typeof v !== 'object') return null;
  const o = v as Record<string, unknown>;
  const motivoBruto = txt(o.motivo);
  return {
    pode: o.pode === true,
    motivo: (motivoBruto as MotivoAcesso | null) ?? null,
    evento: txt(o.evento) ?? '',
    direcao: o.direcao === true,
    dono: o.dono === true,
    resumo: normalizarResumo(o.resumo),
  };
}

// Converte o erro do banco em uma mensagem legível para a pessoa da portaria.
export function mensagemBanco(e: unknown): string {
  // O erro do Supabase é um objeto { message, code, details, hint }.
  // Não é Error. Por isso o `instanceof` escondia tudo.
  const msg =
    (typeof e === 'object' && e !== null && 'message' in e
      ? String((e as { message?: unknown }).message ?? '')
      : e instanceof Error
        ? e.message
        : String(e ?? '')) || '';

  if (!msg) return 'Não conseguimos concluir. Tente de novo.';

  // Mensagens que o banco já escreve em português: mostre como vêm.
  if (/inscri|vaga|valor|sessão|pagamento|CPF|grupo/i.test(msg)) return msg;

  if (/uq_participante_por_conta/i.test(msg))
    return 'Seu cadastro já existe nesta conta. Recarregue a página e tente de novo.';
  if (/permission denied|JWT|not authenticated/i.test(msg))
    return 'Não conseguimos confirmar seu acesso agora. Tente de novo.';
  if (/row-level security/i.test(msg))
    return 'As inscrições não estão abertas para esta participação.';

  // Qualquer outro: MOSTRE. Texto técnico é melhor que silêncio.
  return msg;
}

// Título grande do resultado, conforme a situação.
export function tituloResultado(r: ResultadoLeitura): string {
  switch (r.situacao) {
    case 'entrou':
      return 'Pode entrar';
    case 'cancelada':
      return 'INSCRIÇÃO CANCELADA';
    case 'nao_encontrado':
      return 'INGRESSO NÃO ENCONTRADO';
    case 'outro_evento':
      return 'INGRESSO DE OUTRO EVENTO';
    case 'lista_espera':
      return 'LISTA DE ESPERA';
    case 'nao_liberado':
      return 'NÃO LIBERADO';
    default:
      // 'ja_entrou' e 'nao_pago' já trazem a mensagem com o horário/valor.
      return (r.mensagem || '').toUpperCase().replace(/\.$/, '');
  }
}

let audioCtx: AudioContext | null = null;

function obterAudioCtx(): AudioContext | null {
  try {
    if (!audioCtx) {
      const AC =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      audioCtx = new AC();
    }
    return audioCtx;
  } catch {
    return null;
  }
}

// Cria o contexto de áudio e o acorda (precisa de um toque do usuário no iOS).
export function iniciarAudio(): void {
  const ctx = obterAudioCtx();
  if (!ctx) return;
  try {
    if (ctx.state === 'suspended') void ctx.resume();
  } catch {
    // sem áudio disponível — segue em frente
  }
}

// Bipes curtos e secos, sem precisar de arquivo de áudio.
export function tocarBipes(quantidade: number, frequencia = 880): void {
  const ctx = obterAudioCtx();
  if (!ctx) return;

  try {
    if (ctx.state === 'suspended') void ctx.resume();
    const inicio = ctx.currentTime;
    for (let i = 0; i < quantidade; i += 1) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'square';
      osc.frequency.value = frequencia;
      const t = inicio + i * 0.25;
      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.exponentialRampToValueAtTime(0.3, t + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.15);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(t);
      osc.stop(t + 0.16);
    }
  } catch {
    // sem áudio disponível — segue em frente
  }
}

// Vibração do aparelho (quando suportada).
export function vibrar(padrao: number | number[]): void {
  try {
    if ('vibrate' in navigator) navigator.vibrate(padrao);
  } catch {
    // sem vibração
  }
}